// Supabase storage utility for 3D model uploads
import { supabase, STORAGE_BUCKETS } from '../lib/supabase.js';

/**
 * Upload a file to Supabase storage with retry logic
 * @param {File} file - The file to upload
 * @param {string} bucket - The bucket name
 * @param {string} path - The file path within the bucket
 * @param {Object} options - Upload options
 * @param {number} maxRetries - Maximum number of retry attempts
 * @returns {Promise<Object>} - Upload result with URL and path
 */
export const uploadFileToStorage = async (file, bucket, path, options = {}, maxRetries = 3) => {
  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      console.log(`📤 Uploading file to ${bucket}: ${path} (attempt ${attempt}/${maxRetries})`);
      console.log(`📦 File size: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
      
      // Create an abort controller with timeout
      const abortController = new AbortController();
      const timeout = setTimeout(() => {
        abortController.abort();
      }, 120000); // 120 second timeout (2 minutes)
      
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false,
          ...options
        });

      clearTimeout(timeout);

      if (error) {
        console.error('❌ Upload error:', error);
        throw error;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(path);

      console.log('✅ Upload successful:', { path, publicUrl });
      
      return {
        path: data.path,
        publicUrl,
        bucket
      };
    } catch (error) {
      lastError = error;
      console.error(`❌ Upload failed (attempt ${attempt}/${maxRetries}):`, error.message);
      
      // If this isn't the last attempt, wait before retrying
      if (attempt < maxRetries) {
        const delay = Math.min(1000 * Math.pow(2, attempt - 1), 10000); // Exponential backoff, max 10s
        console.log(`⏳ Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  
  // All retries failed
  console.error('❌ Upload failed after all retries:', lastError);
  throw lastError;
};

/**
 * Delete a file from Supabase storage
 * @param {string} bucket - The bucket name
 * @param {string} path - The file path within the bucket
 * @returns {Promise<boolean>} - Success status
 */
export const deleteFileFromStorage = async (bucket, path) => {
  try {
    console.log(`🗑️ Deleting file from ${bucket}:`, path);
    
    const { error } = await supabase.storage
      .from(bucket)
      .remove([path]);

    if (error) {
      console.error('❌ Delete error:', error);
      throw error;
    }

    console.log('✅ Delete successful:', path);
    return true;
  } catch (error) {
    console.error('❌ Delete failed:', error);
    throw error;
  }
};

/**
 * Generate a unique file path for uploads
 * @param {string} userId - User ID
 * @param {string} fileName - Original file name
 * @param {string} type - File type (original, thumbnail, poster, preview)
 * @returns {string} - Unique file path
 */
export const generateFilePath = (userId, fileName, type = 'original') => {
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substr(2, 9);
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  // Clean the base name
  const baseName = fileName.replace(/\.[^/.]+$/, '').replace(/[^a-zA-Z0-9-_]/g, '_');
  
  return `${userId}/${type}_${timestamp}_${randomId}_${baseName}.${extension}`;
};

/**
 * Upload 3D model files to Supabase storage
 * @param {File} glbFile - The GLB/GLTF file
 * @param {File} thumbnailFile - Optional thumbnail image
 * @param {File} posterFile - Optional poster image
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - Asset URLs and paths
 */
export const upload3DModelAssets = async (glbFile, userId, thumbnailFile = null, posterFile = null) => {
  try {
    console.log('🚀 Starting 3D model asset upload...');
    console.log('GLB file:', glbFile.name, glbFile.size);
    
    // Validate GLB file - ONLY GLB format is accepted (not GLTF)
    const fileName = glbFile.name.toLowerCase();
    if (!fileName.endsWith('.glb')) {
      throw new Error('⚠️ Only GLB files are supported! Please upload a .glb file (not GLTF).');
    }
    
    // Enforce file size limit (50MB max per file)
    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
    const fileSizeMB = (glbFile.size / 1024 / 1024).toFixed(2);
    
    if (glbFile.size > MAX_FILE_SIZE) {
      const error = new Error(
        `❌ File size too large: ${fileSizeMB} MB\n\n` +
        `Maximum allowed size is 50 MB.\n` +
        `Please optimize your GLB file or use a compression tool to reduce its size.`
      );
      error.name = 'FileSizeLimitExceeded';
      throw error;
    }
    
    // Warn about files approaching the limit
    if (glbFile.size > 40 * 1024 * 1024) { // 40MB
      console.warn(`⚠️ Large GLB file detected: ${fileSizeMB} MB (close to 50 MB limit)`);
    }
    
    const results = {};
    const LARGE_FILE_THRESHOLD = 10 * 1024 * 1024; // 10MB
    const isLargeFile = glbFile.size > LARGE_FILE_THRESHOLD;
    
    if (isLargeFile) {
      console.log(`📦 Large file detected (${fileSizeMB} MB) - uploading once to save time...`);
    }
    
    // Upload original 3D model to private bucket
    const originalPath = generateFilePath(userId, glbFile.name, 'original');
    console.log(`⬆️  Uploading original (${fileSizeMB} MB)...`);
    const originalResult = await uploadFileToStorage(
      glbFile, 
      STORAGE_BUCKETS.ORIGINALS, 
      originalPath,
      { contentType: 'model/gltf-binary' }
    );
    
    results.originalUrl = originalResult.publicUrl;
    results.originalPath = originalResult.path;
    
    // For large files (>10MB), reuse the original URL for preview to avoid double upload
    // For smaller files, upload a separate preview copy
    if (isLargeFile) {
      console.log('✅ Large file - using original URL for preview (no duplicate upload)');
      results.previewUrl = originalResult.publicUrl;
      results.previewPath = originalResult.path;
    } else {
      // Generate preview GLB (same as original for now, but could be optimized)
      const previewPath = generateFilePath(userId, glbFile.name, 'preview');
      console.log(`⬆️  Uploading preview copy...`);
      const previewResult = await uploadFileToStorage(
        glbFile, 
        STORAGE_BUCKETS.PREVIEWS, 
        previewPath,
        { contentType: 'model/gltf-binary' }
      );
      
      results.previewUrl = previewResult.publicUrl;
      results.previewPath = previewResult.path;
    }
    
    // Upload thumbnail if provided
    if (thumbnailFile) {
      const thumbnailPath = generateFilePath(userId, glbFile.name, 'thumbnail');
      const thumbnailResult = await uploadFileToStorage(
        thumbnailFile, 
        STORAGE_BUCKETS.PREVIEWS, 
        thumbnailPath,
        { contentType: 'image/webp' }
      );
      
      results.thumbnailUrl = thumbnailResult.publicUrl;
      results.thumbnailPath = thumbnailResult.path;
    }
    
    // Upload poster if provided
    if (posterFile) {
      const posterPath = generateFilePath(userId, glbFile.name, 'poster');
      const posterResult = await uploadFileToStorage(
        posterFile, 
        STORAGE_BUCKETS.PREVIEWS, 
        posterPath,
        { contentType: 'image/webp' }
      );
      
      results.posterUrl = posterResult.publicUrl;
      results.posterPath = posterResult.path;
    }
    
    console.log('✅ 3D model asset upload completed:', results);
    return results;
    
  } catch (error) {
    console.error('❌ 3D model asset upload failed:', error);
    throw error;
  }
};

/**
 * Delete 3D model assets from storage
 * @param {Object} assetPaths - Asset paths object
 * @returns {Promise<boolean>} - Success status
 */
export const delete3DModelAssets = async (assetPaths) => {
  try {
    console.log('🗑️ Deleting 3D model assets...');
    
    const deletePromises = [];
    
    // Delete from originals bucket
    if (assetPaths.originalPath) {
      deletePromises.push(
        deleteFileFromStorage(STORAGE_BUCKETS.ORIGINALS, assetPaths.originalPath)
      );
    }
    
    // Delete from previews bucket
    if (assetPaths.previewPath) {
      deletePromises.push(
        deleteFileFromStorage(STORAGE_BUCKETS.PREVIEWS, assetPaths.previewPath)
      );
    }
    
    if (assetPaths.thumbnailPath) {
      deletePromises.push(
        deleteFileFromStorage(STORAGE_BUCKETS.PREVIEWS, assetPaths.thumbnailPath)
      );
    }
    
    if (assetPaths.posterPath) {
      deletePromises.push(
        deleteFileFromStorage(STORAGE_BUCKETS.PREVIEWS, assetPaths.posterPath)
      );
    }
    
    await Promise.all(deletePromises);
    
    console.log('✅ 3D model assets deleted successfully');
    return true;
    
  } catch (error) {
    console.error('❌ Failed to delete 3D model assets:', error);
    throw error;
  }
};

/**
 * Get public URL for a file in storage
 * @param {string} bucket - The bucket name
 * @param {string} path - The file path
 * @returns {string} - Public URL
 */
export const getPublicUrl = (bucket, path) => {
  const { data: { publicUrl } } = supabase.storage
    .from(bucket)
    .getPublicUrl(path);
  
  return publicUrl;
};

export default {
  uploadFileToStorage,
  deleteFileFromStorage,
  generateFilePath,
  upload3DModelAssets,
  delete3DModelAssets,
  getPublicUrl
};
